import { useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, orgID, postIdempotent, useData, useSession } from '@yingbaba/api';
import type { Confirmation, Milestone, Project, ProjectTemplate, TaskView } from '@yingbaba/types';
import {
  Button,
  DatePicker,
  ErrorNotice,
  Field,
  Modal,
  Notice,
  Select,
  useConfirmLeave,
  useFormDraft,
  useToast,
} from '@yingbaba/ui';
import { Plus } from '@yingbaba/icons';

export function CreateProjectButton({
  title = '',
  category = '',
}: {
  title?: string;
  category?: string;
}) {
  const session = useSession(),
    navigate = useNavigate(),
    qc = useQueryClient(),
    toast = useToast();
  const organization = session.data?.organizations.find((o) => o.id === orgID());
  const [open, setOpen] = useState(false),
    [busy, setBusy] = useState(false),
    [template, setTemplate] = useState('');
  const [error, setError] = useState<unknown>(null);
  const templates = useData<ProjectTemplate[]>('/project-templates', open);
  const chosen = templates.data?.find((v) => v.code === template);
  const id = useId(),
    pending = useRef(false),
    attempt = useRef({ input: '', key: '' });
  const leave = useConfirmLeave();
  const draft = useFormDraft({
    enabled: open,
    busy,
    save: (form) => persist(form, true),
    discard: () => setOpen(false),
  });
  function close() {
    if (!pending.current) leave(() => setOpen(false));
  }
  async function persist(form: HTMLFormElement, leaving = false) {
    if (pending.current || !form.reportValidity()) return false;
    pending.current = true;
    setBusy(true);
    setError(null);
    const payload = { ...Object.fromEntries(new FormData(form)), template_code: template };
    const input = JSON.stringify(payload);
    if (attempt.current.input !== input) attempt.current = { input, key: crypto.randomUUID() };
    try {
      const project = await postIdempotent<Project>('/projects', payload, attempt.current.key);
      draft.clean();
      setOpen(false);
      await qc.invalidateQueries({
        predicate: (q) => String(q.queryKey[0]).startsWith('/workspace/projects'),
      });
      toast('项目已创建');
      if (!leaving) navigate(`/projects/${project.id}`);
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  if (
    !['owner', 'admin', 'manager'].includes(organization?.role || '') ||
    (organization?.lifecycle_status && organization.lifecycle_status !== 'active') ||
    session.data?.account.lifecycle_status === 'closing'
  )
    return null;
  return (
    <>
      <Button
        onClick={() => {
          setTemplate('');
          setError(null);
          attempt.current = { input: '', key: '' };
          setOpen(true);
        }}
      >
        <Plus size={16} />
        创建项目
      </Button>
      <Modal
        open={open}
        onOpenChange={(v) => {
          if (!v) close();
        }}
        title="创建项目"
        description="先建立工作区，再和团队一起完善创作需求、阶段与素材。"
        busy={busy}
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={busy}>
              取消
            </Button>
            <Button type="submit" form={id} loading={busy}>
              创建项目
            </Button>
          </>
        }
      >
        <form
          id={id}
          className="form-stack"
          ref={draft.ref}
          onChange={draft.onChange}
          onSubmit={(e) => {
            e.preventDefault();
            void persist(e.currentTarget);
          }}
        >
          <fieldset className="form-draft-fields" disabled={busy}>
            <Field label="项目名称" htmlFor={`${id}-title`}>
              <input
                id={`${id}-title`}
                name="title"
                className="input"
                required
                minLength={2}
                maxLength={200}
                defaultValue={title}
                placeholder="例如：秋季新品产品视频"
              />
            </Field>
            <Field
              label="制作模板"
              htmlFor={`${id}-template`}
              hint="模板中的任务和阶段可以调整，负责人和日期可稍后安排。"
            >
              <Select
                id={`${id}-template`}
                name="template_code"
                value={template}
                onChange={setTemplate}
                disabled={busy}
                aria-label="制作模板"
              >
                <option value="">空白项目</option>
                {templates.data?.map((v) => (
                  <option key={v.code} value={v.code}>
                    {v.name}
                  </option>
                ))}
              </Select>
            </Field>
            {templates.error ? (
              <div className="project-template-hint">
                <p className="small muted">制作模板暂时无法读取，仍可创建空白项目。</p>
                <Button type="button" variant="ghost" onClick={() => void templates.refetch()}>
                  重新读取模板
                </Button>
              </div>
            ) : null}
            {chosen && (
              <section className="project-template-preview" aria-label="模板内容">
                <strong>
                  {chosen.name} · {chosen.stages.length} 个阶段 /{' '}
                  {chosen.stages.reduce((n, v) => n + v.tasks.length, 0)} 项任务
                </strong>
                <p className="small muted">{chosen.description}</p>
                <ol>
                  {chosen.stages.map((stage) => (
                    <li key={stage.title}>
                      <span>{stage.title}</span>
                      <small>{stage.tasks.join(' · ')}</small>
                    </li>
                  ))}
                </ol>
              </section>
            )}
            <Field label="项目类型（可选）" htmlFor={`${id}-category`}>
              <input
                id={`${id}-category`}
                name="category"
                className="input"
                maxLength={80}
                defaultValue={category}
                placeholder={chosen?.category || '例如：品牌宣传片、产品广告'}
              />
            </Field>
            <Field label="目标与工作范围" htmlFor={`${id}-description`}>
              <textarea
                id={`${id}-description`}
                name="description"
                maxLength={8000}
                rows={3}
                placeholder={chosen?.description || '希望完成什么，需要哪些工作（可稍后补充）'}
              />
            </Field>
            <Field label="计划完成日期" htmlFor={`${id}-deadline`}>
              <DatePicker id={`${id}-deadline`} name="deadline" />
            </Field>
          </fieldset>
          <ErrorNotice error={error} />
        </form>
      </Modal>
    </>
  );
}

export function ProjectStatusButton({
  project,
  tasks,
  milestones,
  confirmations,
}: {
  project: Project;
  tasks: TaskView[];
  milestones: Milestone[];
  confirmations: Confirmation[];
}) {
  const [open, setOpen] = useState(false),
    [status, setStatus] = useState(project.status),
    [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const original = useRef(project),
    pending = useRef(false);
  const qc = useQueryClient(),
    toast = useToast(),
    id = useId();
  const unfinished = tasks.filter((v) => v.status !== 'completed').length;
  const pendingStages = milestones.filter((v) => v.status !== 'completed').length;
  const pendingConfirmations =
    confirmations.filter((v) => v.status === 'pending').length +
    (project.brief?.open_questions?.filter((v) => v.trim()).length || 0);
  const rights = project.brief?.rights || [];
  const unclearRights = rights.filter(
    (v) =>
      !['commercial', 'not_used'].includes(v.status) ||
      (v.status === 'commercial' &&
        (v.license_reference_ids || []).some(
          (id) => !project.brief_license_evidence?.find((e) => e.id === id)?.available,
        )),
  ).length;
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending.current || status === original.current.status) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    try {
      const p = original.current;
      await api(`/projects/${p.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          title: p.title,
          category: p.category,
          description: p.description,
          deadline: p.deadline,
          status,
          expected_revision: p.revision,
        }),
      });
      await qc.invalidateQueries();
      setOpen(false);
      toast(
        status === 'active'
          ? '项目已重新打开'
          : status === 'completed'
            ? '项目已归档'
            : '项目已关闭',
      );
    } catch (e) {
      setError(e);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  return (
    <>
      <Button
        variant="ghost"
        onClick={() => {
          original.current = project;
          setStatus(project.status === 'active' ? 'completed' : 'active');
          setError(null);
          setOpen(true);
        }}
      >
        {project.status === 'active' ? '归档与状态' : '重新打开项目'}
      </Button>
      <Modal
        open={open}
        onOpenChange={setOpen}
        title="管理项目状态"
        description="归档、关闭后仍可查阅资料，需要继续时可重新打开。"
        busy={busy}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>
              取消
            </Button>
            <Button type="submit" form={id} loading={busy} disabled={status === project.status}>
              {status === 'active'
                ? '重新打开项目'
                : status === 'completed'
                  ? '确认归档'
                  : '确认关闭'}
            </Button>
          </>
        }
      >
        <form id={id} className="form-stack" onSubmit={submit}>
          <Field label="项目状态">
            <Select value={status} onChange={setStatus} disabled={busy} aria-label="项目状态">
              <option value="active">进行中</option>
              <option value="completed">已归档</option>
              <option value="cancelled">已关闭</option>
            </Select>
          </Field>
          {status !== 'active' && (
            <>
              <section className="project-archive-check">
                <h3>整理前核对</h3>
                <ul>
                  <li>未完成任务：{unfinished} 项</li>
                  <li>未完成阶段：{pendingStages} 个</li>
                  <li>待确认事项：{pendingConfirmations} 项</li>
                  <li>
                    {rights.length
                      ? `待核对素材权利：${unclearRights} 类`
                      : '尚未记录素材商用权利的核对情况'}
                  </li>
                </ul>
              </section>
              <Notice>
                这些事项会保留，不会自动标为完成。归档只表示整理项目资料，不代表作品已通过验收或取得商用授权。
              </Notice>
              <label className="project-tool-consent">
                <input key={status} type="checkbox" required disabled={busy} />
                我已核对上述事项，确认{status === 'completed' ? '归档' : '关闭'}项目。
              </label>
            </>
          )}
          <ErrorNotice error={error} />
        </form>
      </Modal>
    </>
  );
}
